import type { Messages } from '../i18n';
import type { MapId } from '../world/mapdef';

export type DayId = 'fri' | 'sat' | 'sun';
export const DAY_ORDER: readonly DayId[] = ['fri', 'sat', 'sun'];

export type StatKey = 'energy' | 'mood' | 'study' | 'bladder';
export type ItemKey =
  | 'tea' | 'orange' | 'candy' | 'cookies' | 'cake' | 'strawberries' | 'book' | 'water' | 'riceBall' | 'juice'
  // quest items
  | 'atlas' | 'kite' | 'kitten' | 'letter'
  // gifts and café food
  | 'flowers' | 'scarf' | 'keychain' | 'toy' | 'sandwich' | 'cocoa'
  // phase D: weather, garden, baking
  | 'umbrella' | 'tomato' | 'sunflower' | 'chiffon'
  // story: souvenirs carried between the town, Taipei and Hualien
  | 'postcard' | 'pineappleCake' | 'mochi' | 'pebble' | 'photo';
export const GIFT_ITEMS: readonly ItemKey[] = ['flowers', 'scarf', 'keychain', 'toy', 'cookies', 'candy'];
export const QUEST_ITEMS: readonly ItemKey[] = ['atlas', 'kite', 'kitten', 'letter', 'postcard', 'pineappleCake', 'mochi', 'pebble', 'photo'];
export type ChoreKey = 'shoes' | 'dishes' | 'sister' | 'homework' | 'shower' | 'callDad';
export type PetId = keyof Messages['pets'];
export type Person = 'me' | 'mom' | 'dad' | 'emma';
export type AffectionKey = 'mom' | 'dad' | 'sister' | 'emma' | 'chunbo' | 'xiaoyu' | 'xiaohua' | 'xiaomei' | 'azhe' | 'xiaoqiang' | 'xiaofang';

export const CHORES: readonly ChoreKey[] = ['shoes', 'dishes', 'sister', 'homework', 'shower', 'callDad'];
export const WATER_MAX = 5;

/** Xiaoyu's Friday storyline. */
export const XiaoyuPhase = { Absent: 0, Walking: 1, Seated: 2, Helped: 3, Gone: 4 } as const;
export type XiaoyuPhase = (typeof XiaoyuPhase)[keyof typeof XiaoyuPhase];

export interface Birthday {
  m: number;
  d: number;
}

export interface QuestState {
  step: number;
  done: boolean;
  /** Quest-specific number (hidden bush, next checkpoint...). */
  data?: number;
}

export interface GameState {
  version: 3;
  week: number;
  dayIndex: number;
  /** Minutes since midnight. */
  time: number;
  stats: Record<StatKey, number>;
  stars: number;
  coins: number;
  bag: Record<ItemKey, number>;
  affection: Record<AffectionKey, number>;
  chores: Record<ChoreKey, boolean>;
  /** Flags reset every day: objectives, once-per-day rewards. */
  daily: Record<string, boolean>;
  /** Counters reset every day. */
  counters: Record<string, number>;
  /** Flags that persist for the whole game (birthday parties...). */
  forever: Record<string, boolean>;
  npcTalk: Record<string, number>;
  xiaoyu: { phase: XiaoyuPhase; x: number; y: number };
  player: { map: MapId; x: number; y: number; look: number; pet: PetId; outfit?: number };
  quests: Record<string, QuestState>;
  /** Quest shown by the on-screen guide arrow. */
  tracked: string | null;
  birthdays: Record<Person, Birthday>;
  feed: { solved: string[]; liked: string[]; posted: boolean; comments?: Record<string, { who: string; text: string }[]> };
  /** Spaced-repetition deck: Leitner box and the day index when the word is due. */
  vocab: Record<string, { box: number; due: number }>;
  /** The player's own posts on PixelBook. */
  posts: { id: number; img: string; caption: number; likes: number; comments: { who: string; text: string }[]; day: number }[];
  /** English learning record (the passport). */
  english: EnglishLog;
  // --- Phase D ---------------------------------------------------------------
  /** Balcony garden: nine plots. */
  garden: GardenPlot[];
  /** Mom's morning texts and the replies you picked (last two weeks). */
  momTexts: { day: number; key: string; picks: number[] }[];
  /** What NPCs remember about you (flags per character). */
  memory: Record<string, string[]>;
  /** Growth record: notable things that happened. */
  journal: { day: number; icon: string; key: string; arg?: string }[];
  /** Allowance, piggy bank and spending. */
  money: {
    piggy: number;
    lastAllowanceWeek: number;
    log: Record<number, { need: number; want: number }>;
    wish: string[];
    /** Lantern Bank account (airport package): balance, savings goal and the last movements. */
    bank?: number;
    goal?: number;
    bankLog?: { day: number; kind: 'in' | 'out' | 'interest' | 'goal'; n: number }[];
  };
  /** School grade (3–6): sets the difficulty of worksheets and events. */
  grade: number;
  /** Hobby practice points. */
  hobbies: Record<string, number>;
  /** Sky package: weather forced for a game day number (events, debug helpers). */
  weather?: Record<number, 'sunny' | 'cloudy' | 'rain' | 'storm' | 'cool' | 'typhoon'>;
}

export type Crop = 'strawberry' | 'tomato' | 'sunflower';
export interface GardenPlot {
  crop: Crop | null;
  water: number;
  /** Day number of the last watering (once a day). */
  lastDay: number;
}
export const emptyGarden = (): GardenPlot[] => Array.from({ length: 9 }, () => ({ crop: null, water: 0, lastDay: -1 }));

export interface EnglishLog {
  /** Words spelled right / said right with the microphone, at least once. */
  spelled: Record<string, number>;
  shadowed: Record<string, number>;
  /** Best result per role-play (1–3 stars) and per story. */
  talks: Record<string, number>;
  stories: Record<string, number>;
  /** Travel and festival stamps: id → day number when earned. */
  stamps: Record<string, number>;
  /** Activity per day number, for the weekly summary. */
  days: Record<number, { words: number; talks: number; spells: number; mins: number }>;
}

export function emptyEnglish(): EnglishLog {
  return { spelled: {}, shadowed: {}, talks: {}, stories: {}, stamps: {}, days: {} };
}

export function dayId(s: GameState): DayId {
  return DAY_ORDER[s.dayIndex % DAY_ORDER.length];
}

export interface NewGameOptions {
  look: number;
  pet: PetId;
  birthdays: Record<Person, Birthday>;
}

export const DEFAULT_BIRTHDAYS: Record<Person, Birthday> = {
  me: { m: 5, d: 8 },
  mom: { m: 3, d: 17 },
  dad: { m: 8, d: 20 },
  emma: { m: 6, d: 12 },
};

export function createState(opts: NewGameOptions): GameState {
  return {
    version: 3,
    week: 1,
    dayIndex: 0,
    time: 0,
    stats: { energy: 85, mood: 75, study: 20, bladder: 20 },
    stars: 20,
    coins: 60,
    bag: { tea: 2, orange: 2, candy: 2, cookies: 0, cake: 0, strawberries: 0, book: 1, water: 3, riceBall: 0, juice: 0, atlas: 0, kite: 0, kitten: 0, letter: 0, flowers: 0, scarf: 0, keychain: 0, toy: 0, sandwich: 0, cocoa: 0, umbrella: 0, tomato: 0, sunflower: 0, chiffon: 0, postcard: 0, pineappleCake: 0, mochi: 0, pebble: 0, photo: 0 },
    affection: { mom: 70, dad: 70, sister: 70, emma: 60, chunbo: 50, xiaoyu: 0, xiaohua: 40, xiaomei: 40, azhe: 40, xiaoqiang: 40, xiaofang: 40 },
    chores: { shoes: false, dishes: false, sister: false, homework: false, shower: false, callDad: false },
    daily: {},
    counters: {},
    forever: {},
    npcTalk: {},
    xiaoyu: { phase: XiaoyuPhase.Absent, x: 180, y: 640 },
    player: { map: 'school', x: 120, y: 80, look: opts.look, pet: opts.pet },
    quests: {},
    tracked: null,
    birthdays: structuredClone(opts.birthdays),
    feed: { solved: [], liked: [], posted: false },
    vocab: {},
    posts: [],
    english: emptyEnglish(),
    garden: emptyGarden(),
    momTexts: [],
    memory: {},
    journal: [],
    money: { piggy: 0, lastAllowanceWeek: 0, log: {}, wish: [], bank: 0, goal: 0, bankLog: [] },
    grade: 4,
    hobbies: {},
  };
}

/** Clamp to [0, 100] — every 0..100 meter goes through this. */
export function clamp100(v: number): number {
  return Math.max(0, Math.min(100, v));
}

/** Clamp a birthday to a valid calendar date (Feb 29 allowed). */
export function sanitizeBirthday(b: Partial<Birthday> | undefined, fallback: Birthday): Birthday {
  const m = Math.round(Number(b?.m));
  const d = Math.round(Number(b?.d));
  if (!(m >= 1 && m <= 12)) return { ...fallback };
  const maxDay = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
  if (!(d >= 1 && d <= maxDay)) return { m, d: Math.min(fallback.d, maxDay) };
  return { m, d };
}
